"use client";
import clsx from "clsx";
import { useEffect, useState, useRef } from "react";
import { useForm } from "react-hook-form";
import { AnimateChangeInHeight } from "@/components/animations/animateHeight";
import {
	ComprehensiveForm,
	ComprehensiveLoanDetails,
	ModalFormType,
	SingleExtraRepayment,
} from "@/types/global";
import { comprehensiveCalculator } from "@/assets/calculators/comprehensive/util";
import { LoadingSpinner } from "@/components/ui/loading";
import { motion } from "framer-motion";
import {
	paymentFrequency,
	maxBalloonPercentage,
	loanSecurityType,
	appSettingsConfig,
	EasySites,
	LOAN_PURPOSE_PERSONAL,
	LOAN_PURPOSE_BUSINESS,
	VITE_SHEET_TOKEN,
	YesSites,
	JadeSites,
} from "@/lib/constants";
import { ChevronDown, ChevronRight, ArrowRight, CircleCheckBig } from "lucide-react";
import axios from "axios";
import {
	AreaChart,
	Area,
	XAxis,
	YAxis,
	CartesianGrid,
	Tooltip,
	Legend,
	ResponsiveContainer,
} from "recharts";
import {
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Paper,
	TableFooter,
} from "@mui/material";
import { PartnerPageDetails } from "@/lib/utils";

export function ComprehensiveCalculator({ rootElement }: { rootElement: HTMLElement }) {
	// Initialise form
	const form = useForm<ModalFormType<ComprehensiveForm>>({
		mode: "onBlur",
		defaultValues: {
			interestRate: {
				ongoing: 6.5,
				fixedRate: 6.5,
				fixedTerm: 3,
			},
		},
	});

	const {
		watch,
		setValue,
		register,
		getValues,
		handleSubmit,
		formState: { errors },
	} = form;

	// Root Element Attributes
	const [siteTitle, setSiteTitle] = useState(rootElement.getAttribute("data-title"));
	const [loanTypeOptions, setLoanTypeOptions] = useState(rootElement.getAttribute("loanTypes"));
	const [calculatorType, setCalculatorType] = useState(
		rootElement.getAttribute("calculatorType"),
	);
	const [themeColorRoot, setThemeColorRoot] = useState(rootElement.getAttribute("themeColor"));
	const [themeColorLight, setThemeColorLight] = useState(
		rootElement.getAttribute("themeColorLight"),
	);
	const [themeColorDark, setThemeColorDark] = useState(
		rootElement.getAttribute("themeColorDark"),
	);
	const [hoverColor, setHoverColor] = useState(rootElement.getAttribute("hoverColor"));
	const [themeHeaderTxt, setThemeHeaderTxt] = useState(
		rootElement.getAttribute("themeHeaderTxt"),
	);
	const [breakdownBG, setBreakdownBG] = useState(rootElement.getAttribute("breakdown-bg-color"));
	const [loanMinOverride] = useState(rootElement.getAttribute("loanMin"));
	const [loanMaxOverride] = useState(rootElement.getAttribute("loanMax"));
	const calcChartAttr = rootElement.getAttribute("calcChart");
	const showCharts =
		calcChartAttr === null
			? true
			: !["", "0", "false", "no"].includes(calcChartAttr.toLowerCase());

	// Partner Pages have different minimum loan amounts
	const [loanAmountMin, setLoanAmountMin] = useState(
		loanMinOverride ? Number(loanMinOverride) : 10000,
	);
	const [appSettings, setAppSettings] = useState("Jade Finance");

	const [paymentFreq, setPaymentFreq] = useState(paymentFrequency[0]); // Set to value for calculator to read
	const [selectedLoanType, setSelectedLoanType] = useState<string | undefined>(""); // "Secured", "Unsecured", or "Green" (for Car)
	const [filteredSecurityTypes, setFilteredSecurityTypes] = useState(loanSecurityType);
	const [calcLoanType, setCalcLoanType] = useState<string>(loanTypeOptions ?? "main");
	const [loanRates, setLoanRates] = useState([]);
	const [loading, setLoading] = useState(true);
	const [modalForm, setModalForm] = useState(false);

	const hasFetched = useRef(false);

	// Default values on initial load
	useEffect(() => {
		form.setValue("loanAmount", "30,000");
		setValue("loanTerm", 5);
	}, []);

	useEffect(() => {
		setCalcLoanType(rootElement.getAttribute("loanTypes")?.toLowerCase().trim() ?? "main");
	}, [rootElement]);

	// Apply app theme settings
	useEffect(() => {
		if (
			siteTitle?.includes("Contact") ||
			siteTitle?.includes("Compare") ||
			siteTitle?.includes("Fast Quote") ||
			siteTitle?.includes("No Doc Car Loans")
		) {
			setAppSettings("Jade Finance");
		} else {
			setAppSettings(siteTitle ?? "Jade Finance");
		}
	}, [siteTitle]);

	useEffect(() => {
		if (!rootElement) return;

		rootElement?.style.setProperty(
			`--jFcalc-theme-color`,
			themeColorRoot ?? appSettingsConfig[appSettings].themeColor,
		);
		rootElement?.style.setProperty(
			`--jFcalc-theme-color-light`,
			themeColorLight ?? appSettingsConfig[appSettings].themeColorLight,
		);
		rootElement?.style.setProperty(
			`--jFcalc-theme-color-dark`,
			themeColorDark ?? appSettingsConfig[appSettings].themeColorDark,
		);
		rootElement?.style.setProperty(
			`--jFcalc-theme-h-text`,
			themeHeaderTxt ?? appSettingsConfig[appSettings].themeHeaderTxt,
		);
		rootElement?.style.setProperty(
			`--jFcalc-hover-color`,
			hoverColor ?? appSettingsConfig[appSettings].hoverColor,
		);
		rootElement?.style.setProperty(
			`--jFcalc-breakdown-bg-color`,
			breakdownBG ?? appSettingsConfig[appSettings].breakdownBG,
		);
	}, [
		appSettings,
		appSettingsConfig,
		rootElement,
		themeColorRoot,
		themeColorLight,
		themeColorDark,
		themeHeaderTxt,
		hoverColor,
		breakdownBG,
	]);

	// We will use this as Balloon Payment
	const [singleExtraRepayment, setSingleExtraRepayment] = useState<SingleExtraRepayment>({
		active: false,
		repaymentAmount: 0,
		yearOfPayment: 1,
	});

	// Partner Page Information
	const hasFetchedPartnerPageID = useRef(false);
	// Check if Page is a Partner Page
	// Sets the Minimum Loan Amount
	useEffect(() => {
		if (hasFetchedPartnerPageID.current) return;
		hasFetchedPartnerPageID.current = true;

		const body = document.body;
		const classes = body.className.split(" ");

		// Easy Partner Pages contains "page-template-easy-partner-page" & "page-id-" class name.
		// Compare Partner Pages contains "page-template-jade-plain"     & "page-id-" class name.
		let partnerDetails: PartnerPageDetails | null = null;
		if (classes.includes("page-template-easy-partner-page")) {
			const idClass = classes.find((className) => className.startsWith("page-id-"));
			partnerDetails = {
				partner: "Easy",
				page_post_id: idClass?.replace("page-id-", "") ?? "",
			};
		} else {
			const pageData = (window as any).wpPageData;
			const minAmount = pageData?.loanAmountMinimum;
			if (minAmount) {
				setLoanAmountMin(minAmount);
			} else {
				setLoanAmountMin(10000);
			}
			return;
		}

		if (partnerDetails?.partner === "Easy" && ["256"].includes(partnerDetails?.page_post_id)) {
			switch (partnerDetails?.page_post_id) {
				case "256": // Easy Boat Loans - Gold Coast Boating Centre
					setLoanAmountMin(2000);
					break;
				default:
					setLoanAmountMin(10000);
					break;
			}
		}
	}, [setLoanAmountMin, loanAmountMin]);

	// Factors influencing Interest Rates
	const repaymentType = watch("repaymentType");

	useEffect(() => {
		const listener = (event: { code: string; preventDefault: () => void }) => {
			if (modalForm !== true && (event.code === "Enter" || event.code === "NumpadEnter")) {
				event.preventDefault();
				setModalForm(true);
			}
		};
		document.addEventListener("keydown", listener);
		return () => {
			document.removeEventListener("keydown", listener);
		};
	}, []);

	// Update Loan Details
	useEffect(() => {
		updateLoanDetails();
	}, [
		singleExtraRepayment,
		repaymentType,
		...watch([
			"loanAmount",
			"loanTerm",
			"interestRate.ongoing",
			"interestRate.fixedRate",
			"interestRate.fixedTerm",
		]),
	]);

	// Function to calculate results
	const [loanDetails, setLoanDetails] = useState<ComprehensiveLoanDetails>();
	function updateLoanDetails() {
		setLoanDetails(comprehensiveCalculator(form, singleExtraRepayment));
	}

	// Total Repayment Calculation
	const [totalInterest, setTotalInterest] = useState(0);
	const [totalPrincipal, setTotalPrincipal] = useState(0);

	useEffect(() => {
		if (loanDetails != undefined) {
			const loanDetailsTransformed = loanDetails.annualLoanDetails;
			let totalInterest = 0;
			let totalPrincipal = 0;
			loanDetailsTransformed.map((item) => {
				totalInterest += item.interestPaid;
				totalPrincipal += item.principalPaid;
			});
			setTotalInterest(totalInterest);
			setTotalPrincipal(totalPrincipal);
		}
	}, [loanDetails]);

	// Decimal counter
	function CountDecimalDigits(number: number) {
		var char_array = number.toString().split(""); // split every single char
		var not_decimal = char_array.lastIndexOf(".");
		return not_decimal < 0 ? 0 : char_array.length - not_decimal;
	}

	// Payment Animation
	const [displayValue, setDisplayValue] = useState(0);

	useEffect(() => {
		// Ensure loanDetails is defined before proceeding
		if (loanDetails && loanDetails.variableRepayments !== undefined) {
			const targetValue = isNaN(loanDetails.variableRepayments)
				? 0
				: Math.round(loanDetails.variableRepayments) *
					(paymentFreq.id === 1 ? 1 / 4.38 : paymentFreq.id === 2 ? 1 / 2.19 : 1); // Adjust based on new frequency IDs

			const duration = 0.1; // Duration in seconds
			const startValue = displayValue;

			const start = performance.now();

			const step = (timestamp: number) => {
				const elapsed = (timestamp - start) / 1000;
				const progress = Math.min(elapsed / duration, 1);
				const currentValue = Math.round(startValue + (targetValue - startValue) * progress);

				setDisplayValue(currentValue);

				if (progress < 1) {
					requestAnimationFrame(step);
				}
			};

			requestAnimationFrame(step);
		}
	}, [loanDetails, paymentFreq.id]);

	const handleOpen = () => {
		const loanAmount = formatValueToNumber(getValues("loanAmount"));

		const hostName = window.location.hostname.toLowerCase();
		const JadeDomain = JadeSites.some(
			(domain: string) => hostName === domain || hostName.endsWith("." + domain),
		);
		const EasyDomain = EasySites.some(
			(domain: string) => hostName === domain || hostName.endsWith("." + domain),
		);
		const YesDomain = YesSites.some(
			(domain: string) => hostName === domain || hostName.endsWith("." + domain),
		);

		const isFastQuoteOrCompare = [
			"fast-quote.jade.finance",
			"compare",
			"contact.jade",
			"apl.jadeagrifinance",
			"nodoccarloans",
		].some((keyword) => hostName.includes(keyword));

		if (loanAmount >= loanAmountMin) {
			if (JadeDomain) {
				// [Jade Finance Theme] 86czx4bdc - Calls current site's WordPress Theme's openCustomModal (main.js) which handles pop out Quick Forms
				const openWPThemeModal = (window as any).openCustomModal as
					| undefined
					| ((e?: { preventDefault?: () => void }) => void);
				if (typeof openWPThemeModal === "function") {
					openWPThemeModal({ preventDefault: () => {} });
				} else if (isFastQuoteOrCompare) {
					const $ = (window as any).jQuery;
					if (!$) return;
					$(".jade-fullscreen-quote:not(.consultation)").fadeIn("fast");
				} else {
					console.warn("openCustomModal not found on window");
				}
			} else if (EasyDomain || YesDomain) {
				// [Easy Theme] 86d0eb5h3
				const openWPThemeModal: HTMLElement | null =
					document.getElementById("customModalBackdrop");
				if (openWPThemeModal) {
					openWPThemeModal.classList.remove("hidden");
					document.body.classList.add("no-scroll");
				} else {
					console.warn("customModalBackdrop not found");
				}
			} else {
				console.warn("Site is not supported for this Calculator");
			}
		}
	};

	// Breakdown Toggle
	const [breakdown, setBreakdown] = useState(false);
	const handleToggle = () => {
		setBreakdown((prev) => !prev);
	};

	// API Call for Consumer Calculator only.
	useEffect(() => {
		if (calculatorType === LOAN_PURPOSE_PERSONAL) {
			const fetchLoanRates = async () => {
				if (hasFetched.current) return;
				hasFetched.current = true;

				try {
					// Google Sheet: Comparison Table - Jade Calculator Rates sheet
					const response = await axios.get(
						"https://sheetdb.io/api/v1/vrybmzuxjsj8s?sheet=Jade%20Calculator%20Rates",
						{
							headers: {
								Authorization: `Bearer ${VITE_SHEET_TOKEN}`,
							},
						},
					);
					const data = response.data;

					setLoanRates(data);
					const interestRate = getInterestRate(calcLoanType, "Secured", data);
					if (interestRate !== null) {
						setValue("interestRate.ongoing", interestRate);
					}
				} catch (err) {
				} finally {
					setLoading(false);
				}
			};

			fetchLoanRates();
		}
	}, []);

	const getInterestRate = (
		calcLoanType: string,
		interestType: "Secured" | "Unsecured" | "Green",
		loanRates: any[],
	): number | null => {
		// Sometimes the loan type is main but the interest type selected is Green due to the site being a Car site.
		const effectiveLoanType =
			interestType.toLowerCase() === "green"
				? "car"
				: calcLoanType?.toLowerCase().replace("loan", "").trim();

		const sheetDBLoan = loanRates.find(
			(rate) => rate["Loan Type"].toLowerCase() === effectiveLoanType,
		);

		// If the loan type is found, retrieve the interest rate
		if (sheetDBLoan) {
			const interestRateString = sheetDBLoan[interestType];

			// Check if the rate is available (not an empty string)
			if (interestRateString && interestRateString !== "N/A") {
				// Parse the interest rate string to a number (remove % and convert)
				return parseFloat(interestRateString);
			}
		}
		// Return null if not found or if the interest rate is not available
		return null;
	};

	const handleLoanSelect = (event: { target: { value: string } }) => {
		const selectedValue = event.target.value; // "Secured", "Unsecured", or "Green" (for Car)
		setSelectedLoanType(selectedValue);

		const interestRate = getInterestRate(
			calcLoanType,
			selectedValue as "Secured" | "Unsecured" | "Green",
			loanRates,
		);

		if (interestRate !== null) {
			setValue("interestRate.ongoing", interestRate);
		} else {
			console.warn(
				`Interest rate not available for ${
					loanTypeOptions === "main" ? "this loan type" : calcLoanType
				} with type ${selectedValue}`,
			);
			setValue("interestRate.ongoing", 0); // Set to 0 or some default value
		}
	};

	useEffect(() => {
		if (calculatorType === LOAN_PURPOSE_PERSONAL) {
			let path = "";
			// For Jade.Finance, check the pathname
			// For other sites, check the hostname
			if (
				["jade.finance", "staging.jade.finance"].includes(
					window.location.hostname.toLowerCase(),
				)
			) {
				path = window.location.pathname.toLowerCase();
			} else {
				path = window.location.hostname.toLowerCase();
			}

			const hasCar = path.includes("car");
			const hasCaravan = path.includes("caravan");
			const isCarNotCaravan = hasCar && !hasCaravan;

			if (
				(calculatorType === LOAN_PURPOSE_PERSONAL && isCarNotCaravan) ||
				calcLoanType === "car"
			) {
				setFilteredSecurityTypes(loanSecurityType); // Includes "Green" Loan Security Option
				return;
			}
			// Create a copy of LoanTypes excluding the last element
			setFilteredSecurityTypes(loanSecurityType.slice(0, -1));
		}
	}, [calculatorType, calcLoanType]);

	// data for the graph
	const generateRepaymentData = (
		totalPrincipal: number,
		totalInterest: number,
		loanTermYears: number,
		interestRate: number,
		balloonPayment: number = 0,
	) => {
		const totalMonths = loanTermYears * 12;
		const monthlyInterestRate = interestRate / 100 / 12; // Convert annual interest rate to monthly

		// Calculate monthly payment using the amortisation formula
		let monthlyPayment =
			((totalPrincipal - balloonPayment) * monthlyInterestRate) /
			(1 - Math.pow(1 + monthlyInterestRate, -totalMonths));

		if (singleExtraRepayment.active) {
			monthlyPayment += singleExtraRepayment.repaymentAmount * monthlyInterestRate;
		}

		const data = [];
		const amortisationSchedule = [];

		// Get the current date
		const currentDate = new Date();

		let remainingPrincipal = totalPrincipal;
		let totalInterestPaid = 0; // Track total interest paid

		for (let month = 0; month < totalMonths; month++) {
			// Calculate the interest for the current month based on remaining principal
			const monthlyInterest = remainingPrincipal * monthlyInterestRate;

			// Calculate the principal payment for the current month
			const principalPayment = monthlyPayment - monthlyInterest;

			// Update remaining principal
			remainingPrincipal -= principalPayment;

			// Update total interest paid
			totalInterestPaid += monthlyInterest;

			// Calculate the month and year
			const monthYear = new Date(currentDate.getFullYear(), currentDate.getMonth() + month);
			const monthYearString = monthYear.toLocaleString("default", {
				month: "long",
				year: "numeric",
			});

			// Push the current month's data to the data array
			data.push({
				month: monthYearString,
				principal: Math.max(remainingPrincipal, 0),
				interest: Math.max(totalInterest - totalInterestPaid, 0), // Remaining interest
			});

			// Create amortisation schedule
			amortisationSchedule.push({
				month: monthYearString,
				totalPayment: monthlyPayment.toFixed(2),
				principalPayment: principalPayment.toFixed(2),
				interestPayment: monthlyInterest.toFixed(2),
				balanceLeft: Math.max(remainingPrincipal, 0).toFixed(2),
			});

			// After the last month, apply the balloon payment and adjust the principal payment
			if (singleExtraRepayment.active && month === totalMonths - 1) {
				amortisationSchedule.push({
					month: "Balloon",
					totalPayment: balloonPayment.toFixed(2),
					principalPayment: balloonPayment.toFixed(2),
					interestPayment: 0,
					balanceLeft: Math.max(remainingPrincipal - balloonPayment, 0).toFixed(2),
				});
			}
		}

		// Transform to yearly data
		const yearlyData = [];
		for (let year = 0; year <= loanTermYears; year++) {
			// Changed to < loanTermYears
			const yearData = {
				year: `Year ${year + 1}`,
				principal: 0,
				interest: 0,
			};

			// Take the data at the start of each year
			const startMonth = year * 12;
			if (startMonth < data.length) {
				yearData.principal = Math.round(Math.max(data[startMonth].principal, 0));
				yearData.interest = Math.round(Math.max(data[startMonth].interest, 0));

				// Add balloon payment to principal for all years except the last
				if (year <= loanTermYears - 1) {
					yearData.principal += balloonPayment;
				}
			}
			yearlyData.push(yearData);
		}

		return {
			yearlyData,
			amortisationSchedule, // Include the full amortisation schedule
		};
	};

	const data = generateRepaymentData(
		totalPrincipal,
		totalInterest,
		getValues("loanTerm"),
		getValues("interestRate.ongoing"),
		singleExtraRepayment.repaymentAmount,
	);

	const [balloonPayment, setBalloon] = useState("");
	const [balloonPercentage, setBalloonPercentage] = useState("");
	const [maxBalloon, setMaxBalloon] = useState(0.5);

	useEffect(() => {
		// Get loanTerm from form (assuming getValues is a function to retrieve form values)
		let loanTermNumber = getValues("loanTerm") as number; // Ensure it's treated as a number
		const newMaxBalloon = maxBalloonPercentage[loanTermNumber] || 0;

		setMaxBalloon(newMaxBalloon);

		// Update balloonPercentage and balloonPayment based on maxBalloon
		if (Number(balloonPercentage) > Number(newMaxBalloon * 100)) {
			setBalloonPercentage((newMaxBalloon * 100).toFixed(2)); // Convert to percentage
			const loanAmountNumber = Number(getValues("loanAmount").replace(/,/g, ""));

			// Calculate the balloon payment based on percentage
			const balloonPaymentValue = loanAmountNumber * newMaxBalloon;
			setBalloon(balloonPaymentValue.toFixed(2));
		}
	}, [getValues("loanTerm"), balloonPercentage]); // Runs whenever loanTerm or balloonPayment changes

	const themeColor = getComputedStyle(rootElement ?? document.documentElement)
		.getPropertyValue("--jFcalc-theme-color")
		.trim();

	const formatValueToNumber = (inputValue: string | number | null | undefined): number => {
		if (typeof inputValue === "number") return inputValue;
		if (typeof inputValue !== "string") return 0;
		const cleaned = inputValue.replace(/[^0-9.-]/g, "");
		return cleaned ? Number(cleaned) : 0;
	};

	const validateLoanAmount = (value: string): boolean | string => {
		const numericValue = formatValueToNumber(value);
		if (numericValue < loanAmountMin) {
			return `Minimum loan amount is $${loanAmountMin.toLocaleString()}`;
		}
		return true;
	};

	const handleLoanAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		let formattedValue = formatValueToNumber(e.currentTarget.value);
		const maxAmount = loanMaxOverride
			? Number(loanMaxOverride)
			: calculatorType === LOAN_PURPOSE_BUSINESS
				? 1000000
				: 500000;

		if (formattedValue >= maxAmount) {
			formattedValue = maxAmount;
		}

		setValue("loanAmount", formattedValue.toLocaleString(), {
			shouldValidate: true,
			shouldDirty: true,
			shouldTouch: true,
		});
	};

	return (
		<>
			<form onSubmit={handleSubmit(() => {})}>
				<div className="jFcalc-h-100 lg:jFcalc-w-full">
					<div className="jFcalc-max-w-full md:jFcalc-grid-cols-4 md:jFcalc-grid lg:jFcalc-gap-8 md:jFcalc-gap-4">
						<div className="jFcalc-col-span-2 jFcalc-h-full jFcalc-max-w-full sm:jFcalc-w-full ">
							<div className="jFcalc-bg-white jFcalc-rounded-2xl md:jFcalc-shadow-lg jFcalc-h-full jFcalc-content-center">
								<AnimateChangeInHeight>
									{/* Form Inputs */}
									<div className="jFcalc-px-6 jFcalc-py-4 jFcalc-space-y-6 sm:jFcalc-w-full">
										{/* Row 1 | Loan Amount / Loan Type | Interest Rate */}
										{/* Loan Type */}
										<div className="max-md:jFcalc-space-y-6 md:jFcalc-flex md:jFcalc-gap-6 jFcalc-space-y-0">
											{/* Column 1: Loan Type */}
											{calculatorType === LOAN_PURPOSE_PERSONAL && (
												<div className="jFcalc-flex-1 loan-type">
													<label
														htmlFor="loan-type"
														className="jFcalc-block jFcalc-text-sm jFcalc-font-medium jFcalc-leading-6 jFcalc-text-gray-900"
													>
														Loan Type
													</label>
													<div className="jFcalc-relative jFcalc-mt-0 jFcalc-rounded-md jFcalc-shadow-sm">
														<select
															name="loan-type-input"
															id="loan-type-input"
															onChange={handleLoanSelect}
															value={
																selectedLoanType ||
																filteredSecurityTypes[0].title
															}
															className="focus:jFcalc-ring-gray-900 jFcalc-block jFcalc-w-full jFcalc-rounded-md jFcalc-border-0 jFcalc-py-1.5 jFcalc-pl-7 jFcalc-pr-12 jFcalc-text-gray-900 jFcalc-ring-1 jFcalc-ring-inset jFcalc-ring-gray-300 jFcalc-placeholder:text-gray-400 jFcalc-focus:ring-2 jFcalc-focus:ring-inset sm:jFcalc-text-sm sm:jFcalc-leading-6"
														>
															<option value="" disabled>
																Select...
															</option>

															{filteredSecurityTypes.map(
																(loanType, index) => (
																	<option
																		key={index}
																		value={loanType.id}
																	>
																		{loanType.title}
																	</option>
																),
															)}
														</select>
													</div>
												</div>
											)}

											{/* Column 1: Loan Amount */}
											{calculatorType === LOAN_PURPOSE_BUSINESS && (
												<div className="jFcalc-flex-1">
													<label
														htmlFor="jFcalc-loan-amount"
														className="jFcalc-block jFcalc-text-sm jFcalc-font-medium jFcalc-leading-6 jFcalc-text-gray-900"
													>
														Loan Amount
													</label>
													<div className="jFcalc-relative jFcalc-mt-0 jFcalc-rounded-md jFcalc-shadow-sm">
														<div className="jFcalc-pointer-events-none jFcalc-absolute jFcalc-inset-y-0 jFcalc-left-0 jFcalc-flex jFcalc-items-center jFcalc-pl-3">
															<span className="jFcalc-text-gray-500 sm:text-sm">
																$
															</span>
														</div>
														<input
															{...register("loanAmount", {
																onChange: handleLoanAmountChange,
																validate: validateLoanAmount,
															})}
															defaultValue="30,000"
															className={clsx(
																errors.loanAmount
																	? "jFcalc-ring-red-500 focus:jFcalc-ring-red-500"
																	: "jFcalc-focus:ring-gray-900",
																"jFcalc-block jFcalc-w-full jFcalc-rounded-md jFcalc-border-0 jFcalc-py-1.5 jFcalc-pl-7 jFcalc-pr-12 jFcalc-text-gray-900 jFcalc-ring-1 jFcalc-ring-inset jFcalc-ring-gray-300 jFcalc-placeholder:text-gray-400 jFcalc-focus:ring-2 jFcalc-focus:ring-inset md:jFcalc-text-sm md:jFcalc-leading-6",
															)}
															type="text"
															id="jFcalc-loan-amount"
															aria-describedby="jFcalc-loan-amount"
															inputMode="numeric"
														/>
													</div>
													{errors.loanAmount && (
														<span className="jFcalc-text-xs jFcalc-text-red-500">
															{errors.loanAmount.message}
														</span>
													)}
												</div>
											)}

											{/* Column 2: Interest Rate */}
											<div className="jFcalc-flex-1">
												<label
													htmlFor="interest-rate"
													className="jFcalc-block jFcalc-text-sm jFcalc-font-medium jFcalc-leading-6 jFcalc-text-gray-900"
												>
													Interest Rate
												</label>
												<div className="jFcalc-relative jFcalc-mt-0 jFcalc-rounded-md jFcalc-shadow-sm">
													<input
														type="number"
														value={
															getValues("interestRate.ongoing") !== 0
																? getValues("interestRate.ongoing")
																: ""
														} // Handle nullable value
														onChange={(e) => {
															const value = e.target.value;

															// Limit value to 100 and handle decimal places
															if (Number(value) >= 100) {
																setValue(
																	"interestRate.ongoing",
																	100,
																);
															} else if (
																CountDecimalDigits(Number(value)) >=
																2
															) {
																const limitedValue = Number(
																	Number(value).toFixed(2),
																);
																setValue(
																	"interestRate.ongoing",
																	limitedValue,
																);
															} else {
																setValue(
																	"interestRate.ongoing",
																	Number(value),
																);
															}
														}}
														className="focus:ring-gray-900 jFcalc-block jFcalc-w-full jFcalc-rounded-md jFcalc-border-0 jFcalc-py-1.5 jFcalc-pl-7 jFcalc-pr-12 jFcalc-text-gray-900 jFcalc-ring-1 jFcalc-ring-inset jFcalc-ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset md:jFcalc-text-sm md:jFcalc-leading-6"
														placeholder="Enter your rate" // Placeholder text
													/>
													<div className="jFcalc-pointer-events-none jFcalc-absolute jFcalc-inset-y-0 jFcalc-right-0 jFcalc-flex jFcalc-items-center jFcalc-pr-3">
														<span className="jFcalc-text-gray-500 md:jFcalc-text-sm">
															%
														</span>
													</div>
												</div>
											</div>
										</div>

										{/* Row 2 | Balloon Percentage */}
										{calculatorType === LOAN_PURPOSE_BUSINESS && (
											<div className="max-md:jFcalc-space-y-6 md:jFcalc-flex md:jFcalc-gap-6 md:jFcalc-space-y-0">
												<div className="jFcalc-flex-1">
													<label
														htmlFor="balloon-percentage"
														className="jFcalc-block jFcalc-text-sm jFcalc-font-medium jFcalc-leading-6 jFcalc-text-gray-900"
													>
														Balloon Percentage
													</label>
													<div className="jFcalc-relative jFcalc-mt-0 jFcalc-rounded-md jFcalc-shadow-sm">
														<input
															type="number"
															id="balloon-percentage"
															className="focus:ring-gray-900 jFcalc-block jFcalc-w-full jFcalc-rounded-md jFcalc-border-0 jFcalc-py-1.5 jFcalc-pl-7 jFcalc-pr-12 jFcalc-text-gray-900 jFcalc-ring-1 jFcalc-ring-inset jFcalc-ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset md:jFcalc-text-sm md:jFcalc-leading-6"
															onChange={(e) => {
																const percentageValue = Math.min(
																	Math.max(
																		+e.currentTarget.value,
																		0,
																	),
																	maxBalloon * 100,
																); // Limit between 0 and 100
																const loanAmountNumber = Number(
																	getValues("loanAmount").replace(
																		/,/g,
																		"",
																	),
																);

																// Calculate the balloon payment based on percentage
																const balloonPaymentValue =
																	(loanAmountNumber *
																		percentageValue) /
																	100;
																setBalloon(
																	balloonPaymentValue.toFixed(2),
																); // Set balloon value with 2 decimals
																setBalloonPercentage(
																	String(percentageValue),
																);

																let isActive = true;
																percentageValue == 0
																	? (isActive = false)
																	: (isActive = true);

																// Update singleExtraRepayment state
																setSingleExtraRepayment((prev) => ({
																	...prev,
																	repaymentAmount:
																		balloonPaymentValue,
																	yearOfPayment: 1,
																	active: isActive,
																}));
															}}
															value={
																balloonPercentage
																	? balloonPercentage
																	: 0
															}
														/>
														<div className="jFcalc-pointer-events-none jFcalc-absolute jFcalc-inset-y-0 jFcalc-right-0 jFcalc-flex jFcalc-items-center jFcalc-pr-3">
															<span className="jFcalc-text-gray-500 md:jFcalc-text-sm">
																%
															</span>
														</div>
													</div>
												</div>

												{/* Row 2: Balloon Payment */}
												<div className="jFcalc-flex-1">
													<label
														htmlFor="balloon-payment"
														className="jFcalc-block jFcalc-text-sm jFcalc-font-medium jFcalc-leading-6 jFcalc-text-gray-900"
													>
														Balloon Payment
													</label>
													<div className="jFcalc-relative jFcalc-mt-0 jFcalc-rounded-md jFcalc-shadow-sm">
														<div className="jFcalc-pointer-events-none jFcalc-absolute jFcalc-inset-y-0 jFcalc-left-0 jFcalc-flex jFcalc-items-center jFcalc-pl-3">
															<span className="jFcalc-text-gray-500 sm:text-sm">
																$
															</span>
														</div>
														<input
															className="jFcalc-focus:ring-gray-900 jFcalc-block jFcalc-w-full jFcalc-rounded-md jFcalc-border-0 jFcalc-py-1.5 jFcalc-pl-7 jFcalc-pr-12 jFcalc-text-gray-900 jFcalc-ring-1 jFcalc-ring-inset jFcalc-ring-gray-300 jFcalc-placeholder:text-gray-400 jFcalc-focus:ring-2 jFcalc-focus:ring-inset md:jFcalc-text-sm md:jFcalc-leading-6"
															type="text"
															id="balloon-payment"
															aria-describedby="balloon-payment-description"
															onChange={(e) => {
																let formattedValue =
																	+e.currentTarget.value.replace(
																		/[^0-9]/g,
																		"",
																	);
																const formattedLoanAmount =
																	getValues("loanAmount"); // e.g., "30,000"
																const loanAmountNumber = Number(
																	formattedLoanAmount.replace(
																		/,/g,
																		"",
																	),
																);

																if (
																	formattedValue >=
																	loanAmountNumber -
																		loanAmountNumber *
																			(1 - maxBalloon)
																) {
																	formattedValue =
																		loanAmountNumber -
																		loanAmountNumber *
																			(1 - maxBalloon);
																}

																setBalloonPercentage(
																	String(
																		(
																			(formattedValue /
																				loanAmountNumber) *
																			100
																		).toFixed(2),
																	),
																);

																let isActive = true;
																formattedValue == 0
																	? (isActive = false)
																	: (isActive = true);

																setSingleExtraRepayment((prev) => ({
																	...prev,
																	repaymentAmount: formattedValue,
																	yearOfPayment: 1,
																	active: isActive,
																}));

																const formattedBalloonValue =
																	formattedValue
																		.toString()
																		.replace(
																			/\B(?=(\d{3})+(?!\d))/g,
																			",",
																		);
																setBalloon(formattedBalloonValue);
															}}
															value={
																balloonPayment
																	? Number(
																			balloonPayment,
																		).toLocaleString()
																	: 0
															}
														/>
													</div>
												</div>
											</div>
										)}

										{/* Row 2 | Loan Amount */}
										{calculatorType === LOAN_PURPOSE_PERSONAL && (
											<div className="jFcalc-space-y-6 sm:jFcalc-flex sm:gap-6 sm:space-y-0">
												<div className="jFcalc-flex-1">
													<label
														htmlFor="jFcalc-loan-amount"
														className="jFcalc-block jFcalc-text-sm jFcalc-font-medium jFcalc-leading-6 jFcalc-text-gray-900"
													>
														Loan Amount
													</label>
													<div className="jFcalc-relative jFcalc-mt-0 jFcalc-rounded-md jFcalc-shadow-sm">
														<div className="jFcalc-pointer-events-none jFcalc-absolute jFcalc-inset-y-0 jFcalc-left-0 jFcalc-flex jFcalc-items-center jFcalc-pl-3">
															<span className="jFcalc-text-gray-500 sm:text-sm">
																$
															</span>
														</div>
														<input
															{...register("loanAmount", {
																onChange: handleLoanAmountChange,
																validate: validateLoanAmount,
															})}
															defaultValue="30,000"
															className={clsx(
																errors.loanAmount
																	? "jFcalc-ring-red-500 focus:jFcalc-ring-red-500"
																	: "focus:ring-gray-900",
																"jFcalc-block jFcalc-w-full jFcalc-rounded-md jFcalc-border-0 jFcalc-py-1.5 jFcalc-pl-7 jFcalc-pr-12 jFcalc-text-gray-900 jFcalc-ring-1 jFcalc-ring-inset jFcalc-ring-gray-300 jFcalc-placeholder:text-gray-400 jFcalc-focus:ring-2 jFcalc-focus:ring-inset sm:text-sm sm:leading-6",
															)}
															type="text"
															id="jFcalc-loan-amount"
															aria-describedby="jFcalc-loan-amount"
															inputMode="numeric"
														/>
													</div>
													{errors.loanAmount && (
														<span className="jFcalc-text-xs jFcalc-text-red-500">
															{errors.loanAmount.message}
														</span>
													)}
												</div>
											</div>
										)}

										{/* Row 4 */}
										<div className="jFcalc-space-y-6 sm:flex sm:gap-6 sm:space-y-0">
											{/* Loan Term */}
											<div className="jFcalc-flex-1">
												{/* Button slider for larger screens */}

												<label
													htmlFor="jFcalc-loan-amount"
													className="jFcalc-block jFcalc-text-sm jFcalc-font-medium jFcalc-leading-6 jFcalc-text-gray-900"
												>
													Loan Term
												</label>
												<div className="jFcalc-mt-0 jFcalc-ring-1 jFcalc-ring-gray-300 jFcalc-rounded-full jFcalc-p-1 jFcalc-overflow-hidden jFcalc-text-center">
													<div className="jFcalc-relative jFcalc-flex jFcalc-space-x-0 jFcalc-items-center">
														{" "}
														{/* Change space-x-4 to space-x-0 */}
														<div
															className={`jFcalc-absolute jFcalc-transition-transform jFcalc-duration-300 jFcalc-ease-in-out jFcalc-bg-gray-200 jFcalc-rounded-full`}
															style={{
																transform: `translateX(${
																	(getValues("loanTerm") - 1) * 90
																}%)`, // Adjust based on selected term
																width: "90%", // Ensure it covers the button area
																height: "100%",
																zIndex: -1, // Behind buttons
															}}
														/>
														{[1, 2, 3, 4, 5, 6, 7].map(
															(term, index) => (
																<button
																	key={index}
																	className={`jFcalc-flex-1 jFcalc-rounded-full jFcalc-py-1.5 jFcalc-px-2 sm:jFcalc-px-4 jFcalc-text-gray-900 sm:jFcalc-text-sm sm:leading-6 jFcalc-mx-auto ${
																		getValues("loanTerm") ===
																		term
																			? "jFcalc-bg-[--jFcalc-theme-color]"
																			: ""
																	}`}
																	onClick={() =>
																		setValue("loanTerm", term)
																	}
																>
																	{term}
																</button>
															),
														)}
													</div>
												</div>
											</div>
										</div>

										{/* Row 5 */}
										<div className="jFcalc-flex-1 jFcalc-hidden md:jFcalc-block ">
											<label
												htmlFor="payment-frequency"
												className="jFcalc-block jFcalc-text-sm jFcalc-font-medium jFcalc-leading-6 jFcalc-text-gray-900"
											>
												Payment Frequency
											</label>
											<div className="jFcalc-mt-0 jFcalc-ring-1 jFcalc-ring-gray-300 jFcalc-rounded-full jFcalc-p-1 jFcalc-text-center">
												<div className="jFcalc-flex jFcalc-space-x-4">
													{paymentFrequency.map((freq, index) => (
														<button
															key={index}
															className={`jFcalc-flex-1 jFcalc-rounded-full jFcalc-py-1.5 jFcalc-text-gray-900  sm:jFcalc-text-sm ${
																paymentFreq.id === index + 1
																	? "jFcalc-bg-[--jFcalc-theme-color]"
																	: ""
															} jFcalc-w-full`} // Added w-full here
															onClick={() => setPaymentFreq(freq)}
														>
															{freq.title}{" "}
															{/* Assuming each frequency has a 'title' property */}
														</button>
													))}
												</div>
											</div>
										</div>
									</div>

									{/* Mobile Version */}
									<div className="md:jFcalc-hidden">
										<div className="jFcalc-flex jFcalc-justify-center">
											<div className="jFcalc-h-px jFcalc-bg-black jFcalc-my-2 jFcalc-w-11/12 jFcalc-mb-2" />
										</div>
										<div className="jFcalc-flex jFcalc-flex-col jFcalc-justify-center jFcalc-items-center jFcalc-mt-2 jFcalc-mb-2">
											<p className="jFcalc-text-md jFcalc-font jFcalc-text-gray-900">
												Your Estimated repayments
											</p>
											<div className="jFcalc-text-5xl lg:jFcalc-text-5xl jFcalc-text-black jFcalc-font-bold jFcalc-whitespace-nowrap jFcalc-mt-2 jFcalc-border-black">
												${displayValue.toLocaleString()}
											</div>
										</div>
										<div className="jFcalc-flex-1 jFcalc-mx-4 jFcalc-mt-6">
											<div className="jFcalc-mt-0 jFcalc-border jFcalc-border-gray-300 jFcalc-rounded-full jFcalc-p-1 jFcalc-text-center">
												<div className="jFcalc-flex jFcalc-space-x-4">
													{paymentFrequency.map((freq, index) => (
														<button
															key={index}
															className={`jFcalc-flex-1 jFcalc-rounded-full jFcalc-py-1.5 jFcalc-text-gray-900  jFcalc-text-[14px] sm:jFcalc-text-sm ${
																paymentFreq.id === index + 1
																	? "jFcalc-bg-[--jFcalc-theme-color]"
																	: ""
															} jFcalc-w-full`}
															onClick={() => setPaymentFreq(freq)}
														>
															{freq.title}{" "}
															{/* Assuming each frequency has a 'title' property */}
														</button>
													))}
												</div>
											</div>
										</div>
										<div className="jFcalc-flex jFcalc-justify-center jFcalc-mt-6 jFcalc-mb-6 jFcalc-items-center lg:jFcalc-hidden">
											<button
												id="jFcalc-trigger-cta-mobile"
												type="button"
												onClick={handleOpen}
												className="jFcalc-max-w-full jFcalc-w-full jFcalc-h-8 lg:jFcalc-h-12 jFcalc-items-center jFcalc-rounded-full jFcalc-my-2 jFcalc-flex jFcalc-gap-2 jFcalc-justify-center jFcalc-bg-[--jFcalc-theme-color] hover:jFcalc-bg-[--jFcalc-hover-color] jFcalc-px-3 jFcalc-py-6 jFcalc-mx-4 jFcalc-text-lg lg:jFcalc-text-2xl jFcalc-font-semibold jFcalc-text-black jFcalc-shadow-sm jFcalc-duration-300  hover:-translate-y-1 focus-visible:jFcalc-outline focus-visible:jFcalc-outline-2 focus-visible:jFcalc-outline-offset-2 focus-visible:jFcalc-outline-[--jFcalc-hover-color] jFcalc-text-start"
											>
												GET A QUICK QUOTE
												<ArrowRight className="jFcalc-h-8 jFcalc-w-8 lg:jFcalc-h-10 lg:jFcalc-w-10" />
											</button>
										</div>
										<div className="jFcalc-flex jFcalc-justify-center jFcalc-mt-4 jFcalc-pb-6 jFcalc-items-center">
											<button
												type="button"
												onClick={handleToggle}
												className="jFcalc-flex jFcalc-items-center"
											>
												<p className="jFcalc-mr-2 jFcalc-text-gray-900 jFcalc-pb-0">
													Repayment Breakdown
												</p>
												{breakdown ? (
													<ChevronDown className="jFcalc-text-[--jFcalc-theme-color] jFcalc-transition-transform jFcalc-duration-3000" />
												) : (
													<ChevronRight className="jFcalc-text-[--jFcalc-theme-color] jFcalc-transition-transform jFcalc-duration-3000" />
												)}
											</button>
										</div>
									</div>
								</AnimateChangeInHeight>
							</div>
						</div>

						<div
							className={`jFcalc-top-6 jFcalc-items-center md:jFcalc-shadow-lg jFcalc-mt-0 jFcalc-col-span-2 jFcalc-py-6 jFcalc-h-full jFcalc-bg-[--jFcalc-breakdown-bg-color] jFcalc-text-white jFcalc-rounded-2xl transition-all duration-300 ease-in-out ${
								breakdown ? "jFcalc-block" : "max-md:jFcalc-hidden"
							} md:jFcalc-block`}
							id="jFcalc-repayment-breakdown"
						>
							{/* Text Results */}
							<div className="jFcalc-px-6 jFcalc-flex-col jFcalc-mb-0 jFcalc-h-1/3 jFcalc-mt-0 max-md:jFcalc-hidden md:jFcalc-block jFcalc-justify-between">
								{loanDetails === undefined ? (
									<div className="jFcalc-py-12">
										<LoadingSpinner />
									</div>
								) : (
									<motion.div
										className="jFcalc-flex jFcalc-h-auto jFcalc-gap-1 jFcalc-justify-between"
										layout
										transition={{ duration: 0.6 }}
									>
										{/* Column 1 */}
										<motion.div className="jFcalc-p-6 jFcalc-px-0 jFcalc-rounded-md jFcalc-text-start jFcalc-mx-0 jFcalc-mr-0 xl:jFcalc-w-[10vw] lg:jFcalc-w-[12vw] md:jFcalc-w-[12vw] sm:jFcalc-w-[30vw] jFcalc-flex jFcalc-flex-col">
											<div className="jFcalc-break-inside-avoid jFcalc-mb-2">
												<p className="jFcalc-text-sm md:jFcalc-text-md lg:jFcalc-text-lg jFcalc-font-bold jFcalc-text-white ">
													Your Estimated
													<br />
													Repayments
												</p>
												<div className="jFcalc-flex jFcalc-items-baseline jFcalc-text-left">
													{/* Additional content can go here */}
												</div>
											</div>
										</motion.div>

										{/* Column 2 */}
										<motion.div className="jFcalc-py-6 jFcalc-px-0 jFcalc-rounded-md jFcalc-text-start xl:jFcalc-w-[10vw] lg:jFcalc-w-[12vw] md:jFcalc-w-[12vw] sm:jFcalc-w-[30vw] jFcalc-flex jFcalc-flex-col jFcalc-justify-end jFcalc-flex-grow jFcalc-items-end">
											<div className="jFcalc-break-inside-avoid jFcalc-mb-2">
												<div className="jFcalc-flex jFcalc-items-baseline jFcalc-text-left">
													<div className="jFcalc-text-3xl lg:jFcalc-text-5xl xl:jFcalc-text-6xl jFcalc-text-[--jFcalc-theme-color] jFcalc-font-bold jFcalc-whitespace-nowrap">
														${displayValue.toLocaleString()}
													</div>
													<p className="jFcalc-ml-1 jFcalc-whitespace-nowrap jFcalc-text-md jFcalc-text-white ">
														{`/ ${
															paymentFreq.id === 1
																? "week"
																: paymentFreq.id === 2
																	? "fortnight"
																	: "month"
														}`}
													</p>
												</div>
											</div>
										</motion.div>
									</motion.div>
								)}
							</div>
							<div className="jFcalc-flex jFcalc-justify-center">
								<div className="jFcalc-h-px jFcalc-bg-[--jFcalc-theme-color] jFcalc-my-2 jFcalc-w-11/12 jFcalc-mb-2" />
							</div>

							{/* Total Repayment */}
							<div className="jFcalc-px-6">
								<div className="jFcalc-py-2 jFcalc-px-2 jFcalc-space-y-4">
									<div className="jFcalc-space-y-2">
										<div className="jFcalc-flex jFcalc-justify-between jFcalc-text-md sm:jFcalc-text-lg jFcalc-font-medium">
											<div className="jFcalc-flex jFcalc-space-x-2 jFcalc-items-center">
												<p className="jFcalc-text-white">Total Interest:</p>
											</div>
											<div>
												<p className="jFcalc-text-white">
													${Math.round(totalInterest).toLocaleString()}
												</p>
											</div>
										</div>
										<div className="jFcalc-flex jFcalc-justify-between jFcalc-text-md sm:jFcalc-text-lg jFcalc-font-medium">
											<div className="jFcalc-flex jFcalc-space-x-2 jFcalc-items-center">
												<p className="jFcalc-text-white">
													Total Principal:
												</p>
											</div>
											<div>
												<p className="jFcalc-text-white">
													${Math.round(totalPrincipal).toLocaleString()}
												</p>
											</div>
										</div>
										<div className="jFcalc-flex jFcalc-justify-between jFcalc-text-md sm:jFcalc-text-lg jFcalc-font-medium">
											<div className="jFcalc-flex jFcalc-space-x-2 jFcalc-items-center">
												<p className="jFcalc-text-white">
													Total Repayments:
												</p>
											</div>
											<div>
												<p className="jFcalc-text-white">
													$
													{Math.round(
														totalInterest + totalPrincipal,
													).toLocaleString()}
												</p>
											</div>
										</div>
									</div>
								</div>
							</div>
							{/* Open modal Button */}
							<div className="jFcalc-flex jFcalc-justify-center jFcalc-mt-6 jFcalc-mb-1 jFcalc-items-center jFcalc-b max-md:jFcalc-hidden">
								<button
									id="jFcalc-trigger-cta"
									type="button"
									onClick={handleOpen}
									className="jFcalc-group jFcalc-h-8 lg:jFcalc-h-12 jFcalc-items-center jFcalc-rounded-xl jFcalc-flex jFcalc-gap-2 jFcalc-justify-between jFcalc-bg-[--jFcalc-theme-color] hover:jFcalc-bg-[--jFcalc-hover-color] jFcalc-px-3 jFcalc-py-2 jFcalc-mx-2 jFcalc-text-xl lg:jFcalc-text-2xl jFcalc-font-semibold jFcalc-text-black jFcalc-shadow-sm jFcalc-duration-300 hover:-translate-y-1 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[--jFcalc-hover-color] jFcalc-text-start "
								>
									GET A QUICK QUOTE
									<div className="group-hover:jFcalc-translate-x-1 jFcalc-transition-transform jFcalc-duration-300">
										<ArrowRight className="jFcalc-h-8 jFcalc-w-8 lg:jFcalc-h-10 lg:jFcalc-w-10" />
									</div>
								</button>
							</div>
							<div className="jFcalc-flex jFcalc-justify-center jFcalc-items-center jFcalc-space-x-4 jFcalc-mt-5 jFcalc-mx-2 max-md:jFcalc-hidden">
								<span className="jFcalc-flex jFcalc-items-center">
									<CircleCheckBig className="jFcalc-mr-1 jFcalc-h-5 jFcalc-w-5 jFcalc-text-[--jFcalc-theme-color]" />
									No impact on credit score
								</span>
								<span className="jFcalc-flex jFcalc-items-center ">
									<CircleCheckBig className="jFcalc-mr-1 jFcalc-h-5 jFcalc-w-5 jFcalc-text-[--jFcalc-theme-color]" />
									Low rate{" "}
									{calcLoanType.toLowerCase() !== "main"
										? calcLoanType?.toLowerCase().replace("loan", "")
										: ""}{" "}
									finance
								</span>
							</div>
						</div>

						{/*Column 3 */}
						{calculatorType === LOAN_PURPOSE_BUSINESS && showCharts && (
							<div
								className={`md:jFcalc-shadow-lg jFcalc-top-6 jFcalc-items-center jFcalc-mt-0 jFcalc-col-span-2 jFcalc-py-6 jFcalc-h-full jFcalc-bg-white jFcalc-rounded-2xl transition-all duration-300 ease-in-out md:jFcalc-block jFcalc-mb-4 ${
									breakdown ? "jFcalc-block" : "max-md:jFcalc-hidden"
								} md:jFcalc-block`}
							>
								<div className="jFcalc-flex jFcalc-justify-center jFcalc-mb-4">
									<h2 className="jFcalc-text-lg jFcalc-font-semibold jFcalc-text-gray-900">
										Repayment Breakdown Graph
									</h2>
								</div>
								<ResponsiveContainer width="95%" height={300}>
									<AreaChart
										data={data.yearlyData}
										margin={{
											top: 10,
											right: 20,
											left: 40,
											bottom: 15,
										}} // Adjust margins for legend placement
									>
										<CartesianGrid strokeDasharray="3 3" />
										<XAxis
											label={{
												value: "Term (years)",
												position: "insideBottom",
												offset: -10,
											}} // Center the label at the bottom
										/>
										<YAxis
											label={{
												value: "Amount Owing ($)",
												angle: -90,
												position: "insideLeft",
												offset: -20,
												style: { textAnchor: "middle" }, // Center the text
											}}
											tickFormatter={(value) => value.toLocaleString()}
										/>

										{/* For Tooltip to display amounts on localeString */}
										<Tooltip
											formatter={(value) => Number(value).toLocaleString()}
										/>
										<Legend
											layout="horizontal"
											verticalAlign="top"
											align="right"
											wrapperStyle={{
												backgroundColor: "transparent",
												borderRadius: "5px",
												padding: "5px",
												position: "absolute",
												top: 10,
												right: 10,
												zIndex: 1, // Ensure the legend is above other elements
											}}
										/>
										<Area
											type="monotone"
											dataKey="principal"
											stackId="1"
											stroke={themeColor}
											fill={themeColor}
											fillOpacity={0.6}
										/>
										<Area
											type="monotone"
											dataKey="interest"
											stackId="1"
											stroke="#0f273e"
											fillOpacity={0.3}
											fill="#0f273e"
										/>
									</AreaChart>
								</ResponsiveContainer>
							</div>
						)}

						{/* Column 4 */}
						{calculatorType === LOAN_PURPOSE_BUSINESS && showCharts && (
							<div
								className={`md:jFcalc-shadow-lg jFcalc-top-6 jFcalc-items-center jFcalc-mt-0 jFcalc-col-span-2 jFcalc-py-6 jFcalc-h-full jFcalc-bg-white jFcalc-rounded-2xl transition-all duration-300 ease-in-out md:jFcalc-block ${
									breakdown ? "jFcalc-block" : "max-md:jFcalc-hidden"
								} md:jFcalc-block`}
							>
								<div className="jFcalc-flex jFcalc-justify-center jFcalc-mb-4">
									<h2 className="jFcalc-text-lg jFcalc-font-semibold jFcalc-text-gray-900">
										Amortisation Chart
									</h2>
								</div>
								<TableContainer
									component={Paper}
									sx={{
										width: "100%",
										maxWidth: "100%",
										height: "331px",
										overflowY: "auto",
										overflowX: "auto",
										boxShadow: "none",
									}}
								>
									<Table
										stickyHeader
										sx={{
											tableLayout: "auto",
											textIndent: "0.5rem",
											"& .MuiTableCell-root": {
												fontSize: "16px",
												lineHeight: 1.5,
											},
											"&::-webkit-scrollbar-track": {
												background: "{--jFcalc-theme-color-dark}",
												borderRadius: "10px",
											},
											"&::-webkit-scrollbar-thumb": {
												background: "{--jFcalc-theme-color-dark}",
												borderRadius: "10px",
											},
										}}
									>
										<TableHead>
											<TableRow>
												{[
													"Month",
													"Payment",
													"Principal",
													"Interest",
													"Balance",
												].map((header) => (
													<TableCell
														key={header}
														sx={{
															textIndent: "0.5rem",
															color: themeColor,
															fontWeight: "bold",
															padding: "0px 1rem",
															borderBottom: "1px solid #0f273e",
															borderTop: "1px solid black",
															textAlign: "left",
															whiteSpace: "nowrap", // Prevent text wrapping
														}}
													>
														{header}
													</TableCell>
												))}
											</TableRow>
										</TableHead>
										<TableBody>
											{data.amortisationSchedule.map((entry, index) => (
												<TableRow
													key={index}
													sx={{
														padding: "0 0.5rem",
														backgroundColor:
															index % 2 === 0
																? "transparent"
																: "#f9f9f9",
													}}
												>
													{/* Month */}
													<TableCell
														sx={{
															whiteSpace: "pre",
															textIndent: "0.5rem",
															padding: "1.25rem 0.5rem",
														}}
													>
														{entry.month}
													</TableCell>

													{/* Payment */}
													<TableCell
														sx={{
															whiteSpace: "pre",
															textIndent: "0.5rem",
															padding: "1.25rem 0.5rem",
														}}
													>
														$
														{Number(entry.totalPayment).toLocaleString(
															undefined,
															{
																minimumFractionDigits: 2,
																maximumFractionDigits: 2,
															},
														)}
													</TableCell>

													{/* Principal */}
													<TableCell
														sx={{
															whiteSpace: "pre",
															textIndent: "0.5rem",
															padding: "1.25rem 0.5rem",
														}}
													>
														$
														{Number(
															entry.principalPayment,
														).toLocaleString(undefined, {
															minimumFractionDigits: 2,
															maximumFractionDigits: 2,
														})}
													</TableCell>

													{/* Interest */}
													<TableCell
														sx={{
															whiteSpace: "pre",
															textIndent: "0.5rem",
															padding: "1.25rem 0.5rem",
														}}
													>
														$
														{Number(
															entry.interestPayment,
														).toLocaleString(undefined, {
															minimumFractionDigits: 2,
															maximumFractionDigits: 2,
														})}
													</TableCell>

													{/* Balance */}
													<TableCell
														sx={{
															whiteSpace: "pre",
															textIndent: "0.5rem",
															paddingLeft: "0.5rem",
															paddingRight: "1rem",
														}}
													>
														$
														{Number(entry.balanceLeft).toLocaleString(
															undefined,
															{
																minimumFractionDigits: 2,
																maximumFractionDigits: 2,
															},
														)}
													</TableCell>
												</TableRow>
											))}
										</TableBody>
										<TableFooter>
											<TableRow>
												<TableCell
													colSpan={1}
													sx={{
														padding: "1rem",
														fontWeight: "bold",
														textAlign: "right",
													}}
												>
													Totals:
												</TableCell>
												<TableCell
													sx={{
														padding: "1.25rem 1rem",
													}}
												>
													$
													{Number(
														data.amortisationSchedule.reduce(
															(total, entry) =>
																total +
																parseFloat(entry.totalPayment),
															0,
														),
													).toLocaleString(undefined, {
														minimumFractionDigits: 2,
														maximumFractionDigits: 2,
													})}
												</TableCell>
												<TableCell
													sx={{
														padding: "1.25rem 1rem",
													}}
												>
													$
													{Number(
														data.amortisationSchedule.reduce(
															(total, entry) =>
																total +
																parseFloat(entry.principalPayment),
															0,
														),
													).toLocaleString(undefined, {
														minimumFractionDigits: 2,
														maximumFractionDigits: 2,
													})}
												</TableCell>
												<TableCell
													sx={{
														padding: "1.25rem 1rem",
													}}
												>
													$
													{Number(totalInterest).toLocaleString(
														undefined,
														{
															minimumFractionDigits: 2,
															maximumFractionDigits: 2,
														},
													)}
												</TableCell>
												<TableCell
													sx={{
														padding: "1.25rem 1rem",
													}}
												>
													{/* You can display the final balance if needed */}
												</TableCell>
											</TableRow>
										</TableFooter>
									</Table>
								</TableContainer>
							</div>
						)}
					</div>
				</div>
			</form>
		</>
	);
}
